import type { Me, Tool } from '../../api/types';
import { IconExternal, IconLock } from '../../shared/icons';
import { ToolArt } from '../../shared/icons';
import { unavailableLabel } from '../../shared/format';
import type { Route } from '../../shared/router';
import { useLaunchTool } from '../tools/useLaunchTool';

interface DashboardPageProps {
  me: Me;
  navigate: (route: Route) => void;
}

export function DashboardPage({ me, navigate }: DashboardPageProps) {
  const licensed = me.tools.filter((t) => t.launchable).length;
  const pending = me.tools.length - licensed;
  const launch = useLaunchTool();

  return (
    <div className="panel">
      <div className="panel-hdr">
        <span>Your tools</span>
        <div className="panel-hdr-actions">
          <span className="badge badge-ok">{licensed} available</span>
          {pending > 0 && <span className="badge badge-neutral">{pending} unavailable</span>}
        </div>
      </div>

      <div className="tool-grid">
        {me.tools.map((tool) => (
          <ToolCard
            key={tool.key}
            tool={tool}
            busy={launch.isPending}
            onLaunch={() => launch.mutate(tool.key)}
            onDetails={() => navigate({ name: 'tool', key: tool.key })}
          />
        ))}
      </div>
    </div>
  );
}

interface ToolCardProps {
  tool: Tool;
  busy: boolean;
  onLaunch: () => void;
  onDetails: () => void;
}

function ToolCard({ tool, busy, onLaunch, onDetails }: ToolCardProps) {
  const pending = !tool.launchable;

  return (
    <div className={`tool-card${pending ? ' is-pending' : ' is-launchable'}`}>
      <div className="tool-card-media">
        <ToolArt toolKey={tool.key} />
        <span
          className={`badge tool-card-status ${
            tool.launchable ? 'badge-ok' : tool.status === 'coming_soon' ? 'badge-warn' : 'badge-neutral'
          }`}
        >
          {unavailableLabel(tool.unavailable_reason)}
        </span>
      </div>

      <div className="tool-card-body">
        <div className="tool-card-head">
          <span className="tool-card-name">{tool.name}</span>
          {tool.version && <span className="tool-card-version">v{tool.version}</span>}
        </div>
        <p className="tool-card-summary">{tool.summary}</p>

        {tool.codes && tool.codes.length > 0 && (
          <div className="tool-card-codes">
            {tool.codes.map((code) => (
              <span className="chip" key={code}>
                {code}
              </span>
            ))}
          </div>
        )}

        <div className="tool-card-action">
          {/* A launchable card opens the tool, because that is what the
              button says. The detail page stays one click away rather than
              standing between the user and the thing they came for. */}
          {pending ? (
            <button className="btn btn-block" onClick={onDetails}>
              <IconLock size={11} />
              Details
            </button>
          ) : (
            <>
              <button className="btn btn-primary btn-block" onClick={onLaunch} disabled={busy}>
                <IconExternal size={11} />
                {busy ? 'Opening…' : 'Open tool'}
              </button>
              <button className="link-button" onClick={onDetails}>
                Details
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
